import { createHttpClient } from '@adapters/HttpClient';
import { apiClientWH } from '@shared/services/ApiClient';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';
import { BankAccountsApiAdapter } from '../adapters/BankAccountsApiAdapter';
import { buildPayerBankNameById } from '../lib/resolvePayerBankName';
import type { PayerBanksFilters } from '../models/BankAccount';

export const PAYER_BANKS_QUERY_KEY = 'payerBanks';

const DEFAULT_FILTERS: PayerBanksFilters = { perPage: 100 };

export const usePayerBanks = (filters: PayerBanksFilters = DEFAULT_FILTERS) => {
  const httpClient = useMemo(() => createHttpClient(apiClientWH), []);
  const apiAdapter = useMemo(() => new BankAccountsApiAdapter(httpClient), [httpClient]);
  const resolvedFilters = { ...DEFAULT_FILTERS, ...filters };

  return useQuery({
    queryKey: [PAYER_BANKS_QUERY_KEY, resolvedFilters],
    queryFn: () => apiAdapter.getPayerBanks(resolvedFilters),
    staleTime: 5 * 60 * 1000,
    placeholderData: keepPreviousData,
  });
};

/** Mapa id → nombre reutilizable en tablas, modales y filtros (misma caché que el desplegable) */
export const usePayerBankNameById = (filters?: PayerBanksFilters) => {
  const { data, isLoading, isError, error } = usePayerBanks(filters);

  const payerBanks = useMemo(() => data?.data ?? [], [data?.data]);

  const nameById = useMemo(() => buildPayerBankNameById(payerBanks), [payerBanks]);

  return {
    payerBanks,
    nameById,
    isLoading,
    isError,
    error,
  };
};
